import { useEffect, useState, useCallback, useRef } from 'react';
import { MessageCircle, Plus, Pencil, Trash2, Loader2, X, Copy } from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { toast } from '@/components/ui/toast';
import { ConfirmModal } from '@/components/ConfirmModal';
import api from '@/lib/api';

interface SmsTemplate {
  id: string;
  name: string;
  body: string;
  category: string | null;
  isActive: boolean;
  createdAt: string;
  // 'system'     → super-admin default, read-only until cloned
  // 'customized' → tenant clone of a system default
  // 'own'        → tenant's own template
  source?: 'system' | 'customized' | 'own';
  tenantId?: string | null;
  clonedFromSystemId?: string | null;
}

// Placeholders resolved at send time. Grouped so an admin authoring a
// booking / meeting / support-ticket template can find the vars for
// the event they're targeting instead of the flat "lead only" list
// this used to be. Grouping is UI-only; templates using any of these
// keep working in the workflow engine + booking / lead / ticket
// notification services (all share the same renderTemplate helper).
// Exported so the super-admin SMS Templates editor can reuse the exact
// same picker without duplicating the list — one source of truth.
export const VARIABLE_GROUPS: { label: string; items: { token: string; label: string }[] }[] = [
  {
    label: 'Lead',
    items: [
      { token: '{{lead.name}}', label: 'Lead name' },
      { token: '{{lead.firstName}}', label: 'First name' },
      { token: '{{lead.phone}}', label: 'Lead phone' },
      { token: '{{lead.email}}', label: 'Lead email' },
      { token: '{{lead.leadNo}}', label: 'Lead number' },
      { token: '{{lead.status}}', label: 'Lead status' },
      { token: '{{lead.source}}', label: 'Source' },
      { token: '{{lead.priority}}', label: 'Priority' },
      { token: '{{lead.city}}', label: 'City' },
      { token: '{{lead.state}}', label: 'State' },
      { token: '{{lead.country}}', label: 'Country' },
    ],
  },
  {
    label: 'Counselor',
    items: [
      { token: '{{counselor.name}}', label: 'Counselor name' },
      { token: '{{counselor.email}}', label: 'Counselor email' },
      { token: '{{counselor.phone}}', label: 'Counselor phone' },
    ],
  },
  {
    label: 'Meeting',
    items: [
      { token: '{{eventName}}', label: 'Event name' },
      { token: '{{date}}', label: 'Date' },
      { token: '{{time}}', label: 'Time' },
      { token: '{{duration}}', label: 'Duration' },
      { token: '{{timezone}}', label: 'Timezone' },
      { token: '{{meetingLink}}', label: 'Join link' },
      { token: '{{location}}', label: 'Location' },
      { token: '{{notes}}', label: 'Notes' },
    ],
  },
  {
    label: 'Support ticket',
    items: [
      { token: '{{ticket.number}}', label: 'Ticket #' },
      { token: '{{ticket.subject}}', label: 'Subject' },
      { token: '{{ticket.priority}}', label: 'Priority' },
      { token: '{{ticket.category}}', label: 'Category' },
      { token: '{{ticket.from}}', label: 'Raised by' },
      { token: '{{ticket.message}}', label: 'Message' },
      { token: '{{ticket.link}}', label: 'Ticket link' },
    ],
  },
  {
    label: 'Follow-up reminder',
    items: [
      { token: '{{reminder.dueAt}}', label: 'Due at' },
    ],
  },
  {
    label: 'Organisation',
    items: [
      { token: '{{orgName}}', label: 'Company / brand' },
      { token: '{{supportEmail}}', label: 'Support email' },
    ],
  },
];

// 160 GSM-7 chars = 1 SMS segment; longer messages split (each billed).
function segments(len: number): number {
  if (len === 0) return 0;
  return len <= 160 ? 1 : Math.ceil(len / 153);
}

export function SmsTemplatesPage() {
  const [templates, setTemplates] = useState<SmsTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<SmsTemplate | 'new' | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<SmsTemplate | null>(null);
  // Confirm before cloning a system default — a misclick used to
  // silently create a tenant copy the vendor didn't ask for.
  const [cloneTarget, setCloneTarget] = useState<SmsTemplate | null>(null);
  const [cloning, setCloning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/sms-templates');
      setTemplates(Array.isArray(data) ? data : []);
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to load SMS templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // The old "Add starter templates" button + /sms-templates/seed-defaults
  // endpoint used to live here. Now that starter content ships as
  // super-admin SystemSmsTemplate rows (isPublic=true), tenants see them
  // as "Default" via the merged list — no manual seed action needed.

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/sms-templates/${deleteTarget.id}`);
      toast('success', 'Template deleted');
      setDeleteTarget(null);
      load();
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Delete failed');
    }
  };

  const doClone = async () => {
    if (!cloneTarget) return;
    setCloning(true);
    try {
      await api.post(`/sms-templates/clone/${cloneTarget.id}`);
      toast('success', 'Cloned to your templates — you can edit it now');
      setCloneTarget(null);
      load();
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Clone failed');
    } finally {
      setCloning(false);
    }
  };

  return (
    <DashboardLayout title="SMS Templates">
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-[#6366F1]" /> SMS Templates
              <span className="text-sm font-normal text-slate-400">({templates.length})</span>
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Reusable SMS messages with variables like <code className="rounded bg-slate-100 px-1">{'{{lead.name}}'}</code>. Use them in Workflows (Send SMS) or when messaging a lead.
            </p>
          </div>
          <button
            onClick={() => setEditing('new')}
            className="inline-flex items-center gap-1.5 rounded-lg bg-[#6366F1] hover:bg-[#4F46E5] px-3.5 py-2 text-sm font-semibold text-white shadow-sm"
          >
            <Plus className="h-4 w-4" /> New Template
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : templates.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/40 px-6 py-14 text-center">
            <MessageCircle className="h-10 w-10 mx-auto text-slate-300 mb-3" />
            <p className="text-sm font-semibold text-slate-700">No SMS templates yet</p>
            <p className="text-xs text-slate-500 mt-1 mb-4">Create your first template — you can use variables like {'{{lead.name}}'}.</p>
            <button
              onClick={() => setEditing('new')}
              className="inline-flex items-center gap-1.5 rounded-lg bg-[#6366F1] hover:bg-[#4F46E5] px-3.5 py-2 text-sm font-semibold text-white"
            >
              <Plus className="h-4 w-4" /> New Template
            </button>
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {templates.map(t => {
              const isSystem = t.source === 'system';
              const isCustomized = t.source === 'customized';
              return (
                <div key={t.id} className="rounded-xl border border-slate-200 bg-white p-4 flex flex-col shadow-sm">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <p className="text-sm font-semibold text-slate-900 truncate">{t.name}</p>
                        {isSystem && (
                          <span className="inline-block rounded-full bg-blue-50 px-1.5 py-0.5 text-[9px] font-medium text-blue-700 border border-blue-100 flex-shrink-0">Default</span>
                        )}
                        {isCustomized && (
                          <span className="inline-block rounded-full bg-amber-50 px-1.5 py-0.5 text-[9px] font-medium text-amber-700 border border-amber-100 flex-shrink-0">Customized</span>
                        )}
                      </div>
                      {t.category && <span className="mt-1 inline-block rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-500">{t.category}</span>}
                    </div>
                    {!t.isActive && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-400">Inactive</span>}
                  </div>
                  <p className="mt-2 flex-1 whitespace-pre-wrap text-xs text-slate-600 line-clamp-4">{t.body}</p>
                  <div className="mt-3 flex items-center justify-between border-t border-slate-50 pt-2">
                    <span className="text-[10px] text-slate-400">{t.body.length} chars · {segments(t.body.length)} SMS</span>
                    <div className="flex items-center gap-1">
                      {isSystem ? (
                        // System templates are read-only until cloned. The
                        // clone button converts it into a tenant SmsTemplate
                        // row that shows up as "Customized" — same UX as
                        // /email-templates.
                        <button
                          onClick={() => setCloneTarget(t)}
                          className="inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] font-medium text-[#6366F1] hover:bg-indigo-50"
                          title="Copy this default into your templates so you can edit it"
                        >
                          <Copy className="h-3 w-3" /> Customize
                        </button>
                      ) : (
                        <>
                          <button onClick={() => setEditing(t)} className="rounded p-1.5 text-slate-500 hover:bg-slate-100 hover:text-[#6366F1]" title="Edit"><Pencil className="h-3.5 w-3.5" /></button>
                          <button onClick={() => setDeleteTarget(t)} className="rounded p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600" title="Delete"><Trash2 className="h-3.5 w-3.5" /></button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {editing && (
        <TemplateDrawer
          template={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      <ConfirmModal
        isOpen={!!deleteTarget}
        title="Delete SMS template?"
        message={`"${deleteTarget?.name}" will be permanently removed.`}
        confirmText="Delete"
        onConfirm={doDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <ConfirmModal
        isOpen={!!cloneTarget}
        title="Customize this template?"
        message={`A private copy of "${cloneTarget?.name}" will be added to your templates so you can edit it. The original default stays untouched.`}
        confirmText={cloning ? 'Cloning…' : 'Customize'}
        onConfirm={doClone}
        onCancel={() => (cloning ? null : setCloneTarget(null))}
      />
    </DashboardLayout>
  );
}

// ── Create / edit drawer ─────────────────────────────────────────────────────

function TemplateDrawer({ template, onClose, onSaved }: { template: SmsTemplate | null; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(template?.name ?? '');
  const [category, setCategory] = useState(template?.category ?? '');
  const [body, setBody] = useState(template?.body ?? '');
  const [isActive, setIsActive] = useState(template?.isActive ?? true);
  const [saving, setSaving] = useState(false);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  const insertVar = (token: string) => {
    const el = bodyRef.current;
    if (!el) { setBody(b => b + token); return; }
    const start = el.selectionStart ?? body.length;
    const end = el.selectionEnd ?? body.length;
    const next = body.slice(0, start) + token + body.slice(end);
    setBody(next);
    // restore caret after the inserted token
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + token.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const save = async () => {
    if (!name.trim()) { toast('error', 'Template name is required'); return; }
    if (!body.trim()) { toast('error', 'Message body is required'); return; }
    setSaving(true);
    try {
      const payload = { name: name.trim(), body, category: category.trim() || undefined, isActive };
      if (template) await api.patch(`/sms-templates/${template.id}`, payload);
      else await api.post('/sms-templates', payload);
      toast('success', template ? 'Template updated' : 'Template created');
      onSaved();
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative z-10 flex h-full w-full max-w-md flex-col bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <h3 className="text-base font-bold text-slate-900">{template ? 'Edit SMS Template' : 'New SMS Template'}</h3>
          <button onClick={onClose} className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"><X className="h-5 w-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <div>
            <label className="mb-1 block text-xs font-normal text-slate-600">Template Name *</label>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. New Lead Welcome"
              className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#6366F1]" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-normal text-slate-600">Category</label>
            <input value={category} onChange={e => setCategory(e.target.value)} placeholder="e.g. Follow-up (optional)"
              className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#6366F1]" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-normal text-slate-600">Message *</label>
            <textarea ref={bodyRef} value={body} onChange={e => setBody(e.target.value)} rows={5}
              placeholder="Hi {{lead.name}}, thanks for reaching out!"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#6366F1]" />
            <p className="mt-1 text-[11px] text-slate-400">{body.length} chars · {segments(body.length)} SMS segment(s)</p>
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-normal text-slate-600">Insert variable</label>
            <div className="space-y-2">
              {VARIABLE_GROUPS.map((g) => (
                <div key={g.label}>
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1">{g.label}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {g.items.map(v => (
                      <button
                        key={v.token}
                        type="button"
                        onClick={() => insertVar(v.token)}
                        className="rounded-full border border-indigo-100 bg-indigo-50 px-2 py-1 text-[11px] font-medium text-[#6366F1] hover:bg-indigo-100"
                        title={v.token}
                      >
                        {v.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} className="h-4 w-4 rounded border-slate-300 text-[#6366F1]" />
            Active
          </label>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-slate-100 px-6 py-4">
          <button onClick={onClose} className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100">Cancel</button>
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 rounded-lg bg-[#6366F1] hover:bg-[#4F46E5] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} {template ? 'Save Changes' : 'Create Template'}
          </button>
        </div>
      </div>
    </div>
  );
}
